import { Injectable } from '@angular/core'
import { RegionalApiClientFactory, RegionalApiClientSetupService } from '@ftr/api-regional'
import { ApiClient } from '@ftr/api-shared'
import { addSpeakerToUtterances } from '@ftr/contracts/regional-api/stt'
import { DataStore, Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, mapData } from '@ftr/foundation'
import { SttAggregate, SttAggregateSchema, SttEditBody, SttEditTransaction } from '@ftr/regional-api-contracts'
import { switchMap } from 'rxjs'
import { RealTimeSttWithRemarks } from '../../types'
import { utterancesToRemarks } from '../../utils'

@Injectable({
  providedIn: 'root',
})
export class RealTimeCourtRecordingSttService extends RegionalApiClientSetupService {
  override readonly regionalApiPath = 'stt'

  constructor(regionalApiClientFactory: RegionalApiClientFactory) {
    super(regionalApiClientFactory)
  }

  getSttWithRemarks(courtSystemId: Uuid, recordingId: Uuid, canReadStt: boolean): ApiResult<RealTimeSttWithRemarks> {
    if (!canReadStt) {
      return ApiResult.success({ remarks: [] })
    }

    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(apiClient =>
        this.getSttAggregate(apiClient, courtSystemId, recordingId).pipe(
          mapData(({ utterances, speakers, lastEdited }) => ({
            recordingId,
            remarks: utterancesToRemarks(addSpeakerToUtterances(utterances, speakers)),
            lastEdited: lastEdited ?? undefined,
          })),
        ),
      ),
    )
  }

  sttEdit(courtSystemId: Uuid, recordingId: Uuid, transaction: SttEditTransaction): ApiResult<void> {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(apiClient =>
        this.postEdit(apiClient, courtSystemId, recordingId, {
          transaction,
        }),
      ),
    )
  }

  private postEdit(
    apiClient: ApiClient,
    courtSystemId: Uuid,
    recordingId: Uuid,
    editBody: SttEditBody,
  ): ApiResult<void> {
    return apiClient.post({
      path: `${recordingId}/edit`,
      body: editBody,
      headers: {
        courtSystemId,
        dataStore: DataStore.Regional,
      },
    })
  }

  private getSttAggregate(apiClient: ApiClient, courtSystemId: Uuid, recordingId: Uuid): ApiResult<SttAggregate> {
    return apiClient
      .get({
        path: `${recordingId}`,
        headers: {
          courtSystemId,
          dataStore: DataStore.Regional,
        },
      })
      .pipe(mapData(data => SttAggregateSchema.parse(data)))
  }
}
